import { Link } from "@tanstack/react-router";
import { Plus, Trash2, Users } from "lucide-react";
import { useState } from "react";
import { CustomerCreateDialog } from "@/components/customers/CustomerCreateDialog";
import { ConfirmDialog } from "@/components/shared/confirm-dialog";
import { DataTable } from "@/components/shared/data-table";
import { EmptyState } from "@/components/shared/empty-state";
import { EnumSelect } from "@/components/shared/enum-select";
import { ErrorState } from "@/components/shared/error-state";
import { CursorPagination } from "@/components/shared/pagination";
import { RowActions } from "@/components/shared/row-actions";
import { SearchInput } from "@/components/shared/search-input";
import { StatusBadge } from "@/components/shared/status-badge";
import { Button } from "@/components/ui/button";
import { type Customer, useCustomers, useDeleteCustomer } from "@/hooks/use-customers";
import { DEFAULT_LIMIT, useDataGrid } from "@/hooks/use-data-grid";
import { CUSTOMER_STATUS_OPTIONS } from "@/lib/enums";
import { formatDate } from "@/lib/format";

export function CustomersTable() {
  const grid = useDataGrid({ defaultSort: "createdAt" });
  const [createOpen, setCreateOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<Customer | null>(null);

  const { data, isLoading, isError, error, refetch } = useCustomers(grid.query, grid.pageRequest);
  const deleteCustomer = useDeleteCustomer();

  const customers = data?.data ?? [];
  const total = data?.total ?? 0;
  const hasNext = data?.hasNext ?? false;
  const hasPrev = data?.hasPrev ?? false;

  function handleDeleteConfirm() {
    if (!deleteTarget) return;
    deleteCustomer.mutate(deleteTarget.id, { onSuccess: () => setDeleteTarget(null) });
  }

  const toolbar = (
    <div className="flex items-center gap-4">
      <SearchInput
        value={grid.searchInput}
        onValueChange={grid.setSearch}
        placeholder="Search customers..."
        className="flex-1 max-w-sm"
      />
      <EnumSelect
        value={grid.status}
        onValueChange={grid.setStatus}
        options={CUSTOMER_STATUS_OPTIONS}
        allOption={{ label: "All statuses" }}
        placeholder="All statuses"
        className="w-[160px]"
        aria-label="Filter by status"
      />
      <Button size="sm" className="ml-auto" onClick={() => setCreateOpen(true)}>
        <Plus className="h-4 w-4 mr-1" />
        New customer
      </Button>
    </div>
  );

  if (isError) {
    return (
      <div className="space-y-4">
        {toolbar}
        <ErrorState
          message={error?.message ?? "Failed to load customers"}
          onRetry={() => refetch()}
        />
        <CustomerCreateDialog open={createOpen} onOpenChange={setCreateOpen} />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {toolbar}

      <DataTable
        headers={[
          { label: "Name", sortKey: "name" },
          { label: "Status", sortKey: "status" },
          { label: "Email", className: "hidden md:table-cell", sortKey: "email" },
          { label: "Created", className: "hidden md:table-cell", sortKey: "createdAt" },
          { label: "", className: "w-12" },
        ]}
        sort={grid.sort}
        sortDir={grid.sortDir}
        onSort={grid.toggleSort}
        loading={isLoading}
        empty={
          <EmptyState
            icon={Users}
            title="No customers found"
            description="Try adjusting your search or filters."
          />
        }
      >
        {customers.map((customer) => (
          <tr
            key={customer.id}
            className="border-b border-border last:border-0 hover:bg-muted/30 transition-colors"
          >
            <td className="px-4 py-3 font-medium">
              <Link
                to="/dashboard/customers/$customerId"
                params={{ customerId: customer.id }}
                className="hover:text-primary transition-colors"
              >
                {customer.name}
              </Link>
            </td>
            <td className="px-4 py-3">
              <StatusBadge status={customer.status} />
            </td>
            <td className="hidden md:table-cell px-4 py-3 text-muted-foreground max-w-xs truncate">
              {customer.email}
            </td>
            <td className="hidden md:table-cell px-4 py-3 text-muted-foreground">
              {formatDate(customer.createdAt)}
            </td>
            <td className="px-4 py-3 text-right">
              <RowActions
                actions={[
                  {
                    label: "Delete",
                    icon: Trash2,
                    destructive: true,
                    onSelect: () => setDeleteTarget(customer),
                  },
                ]}
              />
            </td>
          </tr>
        ))}
      </DataTable>

      <CursorPagination
        total={total}
        page={grid.page}
        limit={DEFAULT_LIMIT}
        hasNext={hasNext}
        hasPrev={hasPrev}
        onNext={() => grid.goNext(data?.nextCursor ?? null)}
        onPrev={() => grid.goPrev(data?.prevCursor ?? null)}
        loading={isLoading}
        itemLabel="customers"
      />

      <CustomerCreateDialog open={createOpen} onOpenChange={setCreateOpen} />
      <ConfirmDialog
        open={deleteTarget !== null}
        onOpenChange={(open) => {
          if (!open) setDeleteTarget(null);
        }}
        title="Delete customer"
        description={`Delete ${deleteTarget?.name ?? "this customer"}? Their orders will be removed too. This cannot be undone.`}
        confirmLabel="Delete"
        variant="destructive"
        loading={deleteCustomer.isPending}
        onConfirm={handleDeleteConfirm}
      />
    </div>
  );
}
